import ChevronRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {Tooltip, Typography, useTheme} from '@mui/material';
import Grid from '@mui/material/Grid';
import {shape, string} from 'prop-types';

import {BREAKPOINTS} from '../../../const';
import {useIsModuleVisibleOrEnabled} from '../../../hooks/components/report-modules/useIsModuleVisibleOrEnabled/useIsModuleVisibleOrEnabled';
import useBreakpoint from '../../../hooks/dom/useBreakpoint';
import useMainSidebar from '../../../hooks/providers/useMainSidebar';
import useReportViews from '../../../hooks/providers/useReportViews';
import useReportModuleClickHandler from '../../embed/hooks/useReportModuleClickHandler/useReportModuleClickHandler';
import MorePopperArrow from '../MorePopperArrow/MorePopperArrow';

// eslint-disable-next-line complexity
const SingleReportModule = ({module}) => {
  const {moduleSubmenuOpen, setModuleSubmenuOpen} = useMainSidebar();
  const {setIsChooseViewPopperOpen, setChooseViewPopperAnchorEl} = useReportViews();
  const {isModuleVisible, isModuleEnabled} = useIsModuleVisibleOrEnabled();

  const isModuleVisibleForCurrentReport = isModuleVisible(module);
  const handleModuleClick = useReportModuleClickHandler();

  const theme = useTheme();
  const moduleEnabled = isModuleEnabled(module);
  const currentBreakpoint = useBreakpoint();
  const isSmallScreen = currentBreakpoint === BREAKPOINTS.xs || currentBreakpoint === BREAKPOINTS.sm;

  const isModuleSubmenuOpen = moduleSubmenuOpen === module.id;

  const onMouseEnter = e => {
    if (!moduleEnabled || isSmallScreen) return;

    if (!module.hasSubMenu) {
      setModuleSubmenuOpen(null);

      // For now, we only have ONE module that has a submenu. If we have more later, we'll implement a proper dynamic solution
      setChooseViewPopperAnchorEl(null);
      setIsChooseViewPopperOpen(false);
      return;
    }

    setModuleSubmenuOpen(module.id);

    setChooseViewPopperAnchorEl(e.currentTarget);
    setIsChooseViewPopperOpen(true);
  };

  if (isModuleVisibleForCurrentReport) {
    return (
      <Tooltip
        PopperProps={{
          disablePortal: true
        }}
        title={moduleEnabled ? '' : module.disabledTooltip}
      >
        <Grid
          data-testid="single-report-module"
          container
          alignItems="center"
          flexWrap="nowrap"
          justifyContent="space-between"
          position="relative"
          sx={{
            '&:hover p': {
              color: moduleEnabled ? theme.palette.secondary.main : 'text.disabled'
            },
            cursor: moduleEnabled ? 'pointer' : 'not-allowed'
          }}
        >
          <Typography
            width="100%"
            onMouseEnter={onMouseEnter}
            whiteSpace="nowrap"
            disabled={!moduleEnabled}
            onClick={moduleEnabled ? e => handleModuleClick(module, e) : null}
            py={isSmallScreen ? 1.5 : 1}
            fontSize={isSmallScreen ? 17 : 13}
            color={moduleEnabled ? 'text.primary' : 'lightgrey'}
          >
            {module.name}
          </Typography>
          {module.hasSubMenu && !isModuleSubmenuOpen ? <ChevronRightIcon color={moduleEnabled ? 'primary' : 'disabled'} sx={{ml: 1}} fontSize="small" /> : null}

          {module.hasSubMenu && isModuleSubmenuOpen && !isSmallScreen ? (
            <MorePopperArrow
              style={{
                position: 'absolute',
                bottom: 0,
                left: 'calc(100% + 8px)'
              }}
            />
          ) : null}
        </Grid>
      </Tooltip>
    );
  }

  return null;
};

SingleReportModule.propTypes = {
  module: shape({
    name: string.isRequired,
    id: string.isRequired
  }).isRequired
};

export default SingleReportModule;
